import { format, isToday, isYesterday, parseISO, startOfDay } from 'date-fns'
import { Message } from '../types/chat'

/**
 * Mention parsing utilities
 */
export interface MentionMatch {
  text: string // The full mention text including @
  userId: string // The user ID mentioned
  startIndex: number // Start position in the original text
  endIndex: number // End position in the original text
}

export interface GroupedMessages {
  dateLabel: string
  dateKey: string
  messages: Message[]
}

/**
 * Group messages by date (Today, Yesterday, or formatted date)
 */
export function groupMessagesByDate(messages: Message[]): GroupedMessages[] {
  const groups: Map<string, Message[]> = new Map()

  messages.forEach((message) => {
    const messageDate = parseISO(message.createdAt)
    const dateKey = startOfDay(messageDate).toISOString()

    if (!groups.has(dateKey)) {
      groups.set(dateKey, [])
    }
    groups.get(dateKey)!.push(message)
  })

  // Convert map to array and sort by date (most recent first)
  return Array.from(groups.entries())
    .map(([dateKey, messages]) => {
      const messageDate = parseISO(dateKey)
      let dateLabel: string
      if (isToday(messageDate)) {
        dateLabel = 'Today'
      } else if (isYesterday(messageDate)) {
        dateLabel = 'Yesterday'
      } else {
        dateLabel = format(messageDate, 'EEEE, MMMM d, yyyy')
      }
      return {
        dateLabel,
        dateKey,
        messages: messages.sort((a, b) => 
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        ),
      }
    })
    .sort((a, b) => 
      new Date(b.dateKey).getTime() - new Date(a.dateKey).getTime()
    )
}

/**
 * Format timestamp for display (relative or absolute)
 */
export function formatMessageTime(dateString: string): string {
  const date = parseISO(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return format(date, 'h:mm a')
  
  if (diffDays === 1) return `Yesterday at ${format(date, 'h:mm a')}`
  if (diffDays < 7) return format(date, 'EEEE at h:mm a')
  
  return format(date, 'MMM d, yyyy at h:mm a')
}

/**
 * Get full timestamp for tooltip/hover
 */
export function getFullTimestamp(dateString: string): string {
  const date = parseISO(dateString)
  return format(date, 'PPpp') // e.g., "Apr 29, 2024 at 2:30 PM"
}

/**
 * Parse mentions from text (format: @username or @userId)
 * Returns array of MentionMatch objects
 */
export function parseMentions(text: string, userMap?: Map<string, { username?: string; firstName?: string; lastName?: string }>): MentionMatch[] {
  const mentions: MentionMatch[] = []
  // Match @username or @userId patterns
  const mentionRegex = /@(\w+)/g
  let match

  while ((match = mentionRegex.exec(text)) !== null) {
    const mentionText = match[0]
    const identifier = match[1]
    const matchIndex = match.index
    
    if (matchIndex === undefined) continue
    
    // Try to find user by username, firstName, or ID
    let userId: string | undefined
    if (userMap) {
      for (const [id, user] of userMap.entries()) {
        const firstWordOfFirstName = user.firstName?.trim().split(/\s+/)[0]
        if (
          user.username === identifier ||
          id === identifier ||
          firstWordOfFirstName?.toLowerCase() === identifier.toLowerCase()
        ) {
          userId = id
          break
        }
      }
    }
    
    // Always highlight mentions for visual consistency, even if user not found
    mentions.push({
      text: mentionText,
      userId: userId || identifier, // Use identifier as fallback if user not found
      startIndex: matchIndex,
      endIndex: matchIndex + mentionText.length,
    })
  }

  return mentions
}

/**
 * Extract mentioned user IDs from text
 */
export function extractMentionedUserIds(
  text: string,
  userMap?: Map<string, { username?: string; firstName?: string; lastName?: string }>
): string[] {
  const mentions = parseMentions(text, userMap)
  const userIds = mentions.map((m) => m.userId)
  // Remove duplicates
  return Array.from(new Set(userIds))
}

